import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { showModal } from 'sh-www/utils/modal-controls';
import { APPLICATION } from 'sh-www/models/application';
import { ASSIGNMENT_INVITE } from 'sh-www/models/assignment-invite';

export const POSTING_ADMIN_SUB_SECTIONS = {
  PENDING: 'Pending',
  HIRED: 'Hired',
  DECLINED: 'Declined',
}

/**
 * Map from the tab name => the application status enumeration.
 *
 * @type {Object}
 */
 export const APPLICATION_STATUS_TO_POSTING_ADMIN_SUB_SECTION_STATUS_MAP = {
  [POSTING_ADMIN_SUB_SECTIONS.PENDING]: APPLICATION.STATUS.PENDING,
  [POSTING_ADMIN_SUB_SECTIONS.DECLINED]: APPLICATION.STATUS.DECLINED,
};

/**
 * Map from the tab name => the application simple status enumeration.
 *
 * @type {Object}
 */
 export const APPLICATION_STATUS_TO_POSTING_ADMIN_SUB_SECTION_SIMPLE_STATUS_MAP = {
  [POSTING_ADMIN_SUB_SECTIONS.PENDING]: APPLICATION.SIMPLE_STATUS.PENDING,
  [POSTING_ADMIN_SUB_SECTIONS.HIRED]: APPLICATION.SIMPLE_STATUS.HIRED,
  [POSTING_ADMIN_SUB_SECTIONS.DECLINED]: APPLICATION.SIMPLE_STATUS.DECLINED,
};

/** @module */
class PostingIndexController extends Controller {
  queryParams = [{
    postingAdminSubSectionView: 'tab',
  }];

  /** @type {Ember.Service} */
  @service('commands/assignment-commands') assignmentCommandService;

  /** @type {Ember.Service} */
  @service('commands/activity-feed-item-commands') activityFeedItemCommands;

  /** @type {Ember.Service} */
  @service('error-tracker') errorTracker;

  /** @type {Ember.Service} */
  @service session;

  /** @type {Ember.Service} */
  @service shData;

  assignmentInvites = [];

  /** @type {String<POSTING_ADMIN_SUB_SECTIONS.*>} The state of the sub nav in the posting view. */
  postingAdminSubSectionView = POSTING_ADMIN_SUB_SECTIONS.PENDING;

  @computed('postingAdminSubSectionView','declinedApplications','pendingApplications','hiredApplications')
  get applicationsFiltered() {
    const activeTab = this.postingAdminSubSectionView;

    if (POSTING_ADMIN_SUB_SECTIONS.DECLINED === activeTab) {
      return this.declinedApplications;
    } else if (POSTING_ADMIN_SUB_SECTIONS.PENDING === activeTab) {
      return this.pendingApplications;
    } else if (POSTING_ADMIN_SUB_SECTIONS.HIRED === activeTab) {
      return this.hiredApplications;
    }

    return [];
  }

  @computed('postingAdminSubSectionView')
  get applicationSimpleStatusFilter() {
    return APPLICATION_STATUS_TO_POSTING_ADMIN_SUB_SECTION_SIMPLE_STATUS_MAP[this.postingAdminSubSectionView];
  }

  @computed('postingAdminSubSectionView')
  get applicationStatusFilter() {
    return APPLICATION_STATUS_TO_POSTING_ADMIN_SUB_SECTION_STATUS_MAP[this.postingAdminSubSectionView];
  }

  @computed('model.assignment')
  get declinedApplications() {
    const { applications } = this.model.assignment;

    return applications.filter(({status}) => {
      return Number(status) < 0;
    });
  }

  @computed('model.assignment')
  get hiredApplications() {
    const { applications } = this.model.assignment;

    return applications.filter(({status}) => {
      return Number(status) >= 3;
    });
  }

  @computed('model.assignment')
  get pendingApplications() {
    const { applications } = this.model.assignment;

    return applications.filter(({status}) => {
      return Number(status) >= 0 && Number(status) < 3;
    });
  }

  @computed('postingAdminSubSectionView','pendingApplications','hiredApplications','declinedApplications')
  get postingAdminNavItems() {
    const navItems = [];
    const currentSubSectionState = this.postingAdminSubSectionView;
    const isNavActive = (stateToCheck) => currentSubSectionState === stateToCheck;

    navItems.push({
      title: POSTING_ADMIN_SUB_SECTIONS.PENDING,
      isActive: () => isNavActive(POSTING_ADMIN_SUB_SECTIONS.PENDING),
      count: this.pendingApplications.length,
    });

    navItems.push({
      title: POSTING_ADMIN_SUB_SECTIONS.HIRED,
      isActive: () => isNavActive(POSTING_ADMIN_SUB_SECTIONS.HIRED),
      count: this.hiredApplications.length,
    });

    navItems.push({
      title: POSTING_ADMIN_SUB_SECTIONS.DECLINED,
      isActive: () => isNavActive(POSTING_ADMIN_SUB_SECTIONS.DECLINED),
      count: this.declinedApplications.length,
    });

    return navItems;
  }

  /**
   * @param {String} one of POSTING_ADMIN_SUB_SECTIONS
   */
  @action
  setActiveTab(subSection) {
    this.set('postingAdminSubSectionView', subSection);
  }

  /**
   * @param {DS.Model} assignment
   * @param {boolean} favorite
   */
  @action
  setFavorite(assignment, favorite) {
    return this.assignmentCommandService.setFavorite(assignment, this.session.currentUser.id, favorite);
  }

  /**
   * @param {DS.Model} assignment
   */
  @action
  showFeedback(assignment) {
    if (this.setNotInterested.isRunning) return;

    const notInterested = assignment.assignmentUserMeta?.notInterested;
    if (notInterested || !assignment.allowSellerFeedback) {
      this.setNotInterested.perform(assignment, !notInterested);
    } else {
      showModal(this, 'posting-feedback', {
        assignment,
      });
    }
  }

  /**
   * @param {DS.Model} assignment
   */
  @action
  showReferralModal(assignment) {
    showModal(this, 'refer-a-friend', { assignment });
  }

  /**
   * @param {DS.Model} assignment
   */
  @action
  markViewed(assignment) {
    this.activityFeedItemCommands.markRecentPostingItemRead(assignment);
  }

  @task
  *loadAssignmentInvitesTask(assignment) {
    try {
      const filters = { source: ASSIGNMENT_INVITE.SOURCES.SOURCE_DIY };
      const includes = 'user.company';
      const response = yield this.assignmentCommandService.loadAssignmentInvites(assignment, filters, includes);
      this.shData.pushResponseToStore(response);
      const assignmentInvites = this.shData.getModelArrayFromResponse(response, 'assignmentInvite');
      this.set('assignmentInvites', assignmentInvites);
    } catch (error) {
      this.errorTracker.error(`loadAssignmentInvitesTask failed for posting id ${this.model.assignment.id}`, this, error);
      throw error;
    }
  }

  @task
  *setNotInterested(assignment, notInterested) {
    yield this.assignmentCommandService.setNotInterested(assignment, this.session.currentUser.id, {}, notInterested);
  }
}

export default PostingIndexController;
